import React, { useState } from 'react';
import { 
  Database, 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Loader2,
  Upload,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';

export default function ConnectionWizard({ onClose, onSave, initialData = null }) {
  const [step, setStep] = useState(initialData ? 2 : 1); // Skip to step 2 if pre-filled
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState(initialData ? {
    name: initialData.name || '',
    description: initialData.description || initialData.purpose || '',
    dbType: initialData.type?.toLowerCase().includes('postgresql') ? 'postgresql' : 
            initialData.type?.toLowerCase().includes('mysql') ? 'mysql' : 
            initialData.type?.toLowerCase().includes('sql server') ? 'sqlserver' : '',
    connectionMethod: initialData.connection?.toLowerCase().includes('ssh') ? 'ssh' : 'direct',
    host: initialData.config?.host || '',
    port: initialData.config?.port || '',
    database: initialData.config?.database || '',
    username: initialData.config?.username || '',
    password: initialData.config?.password || '',
    sshHost: initialData.config?.sshHost || '',
    sshPort: initialData.config?.sshPort || 22,
    sshUsername: initialData.config?.sshUsername || '',
    sshPrivateKey: initialData.config?.sshKey || '',
    sslEnabled: initialData.config?.ssl === 'require' || initialData.config?.ssl === true,
    readOnly: true
  } : {
    name: '',
    description: '',
    dbType: '',
    connectionMethod: 'direct',
    host: '',
    port: '',
    database: '',
    username: '',
    password: '',
    sshHost: '',
    sshPort: 22,
    sshUsername: '',
    sshPrivateKey: '',
    sslEnabled: true,
    readOnly: true
  });

  const dbTypes = [
    { id: 'postgresql', name: 'PostgreSQL', icon: '🐘', defaultPort: 5432 },
    { id: 'mysql', name: 'MySQL', icon: '🐬', defaultPort: 3306 },
    { id: 'sqlserver', name: 'SQL Server', icon: '🗄️', defaultPort: 1433 },
    { id: 'oracle', name: 'Oracle', icon: '🔶', defaultPort: 1521, disabled: true }
  ];

  const handleDbTypeSelect = (type) => {
    const dbType = dbTypes.find(db => db.id === type);
    setFormData({
      ...formData,
      dbType: type,
      port: dbType.defaultPort
    });
    setStep(2);
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setTestResult(null);

    console.log('Testing connection with formData:', formData);

    try {
      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'test-connection',
          data: formData
        })
      });

      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      // Get current user from context or localStorage
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';

      console.log('Saving connection with userId:', userId, 'formData:', formData);

      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save-connection',
          data: {
            ...formData,
            userId
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        onSave(result.connection);
        onClose();
      } else {
        setTestResult({ success: false, error: result.error });
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({ ...formData, sshPrivateKey: event.target.result });
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/20 rounded-lg p-2">
              <Database className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Add External Database</h2>
              <p className="text-sm text-slate-400">Step {step} of 4</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ' + (
                  s < step ? 'bg-green-500 text-white' :
                  s === step ? 'bg-blue-500 text-white' :
                  'bg-slate-700 text-slate-400'
                )}>
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && (
                  <div className={'flex-1 h-1 mx-2 ' + (
                    s < step ? 'bg-green-500' : 'bg-slate-700'
                  )} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-400">
            <span>Database Type</span>
            <span>Connection</span>
            <span>Configuration</span>
            <span>Test & Save</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Database Type */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Select Database Type</h3>
              <div className="grid grid-cols-2 gap-4">
                {dbTypes.map((db) => (
                  <button
                    key={db.id}
                    onClick={() => !db.disabled && handleDbTypeSelect(db.id)}
                    disabled={db.disabled}
                    className={'p-6 rounded-lg border-2 transition-all ' + (
                      formData.dbType === db.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : db.disabled
                        ? 'border-slate-700 bg-slate-700/20 opacity-50 cursor-not-allowed'
                        : 'border-slate-700 bg-slate-700/30 hover:border-blue-500/50 cursor-pointer'
                    )}
                  >
                    <div className="text-4xl mb-3">{db.icon}</div>
                    <div className="text-lg font-semibold text-white">{db.name}</div>
                    {db.disabled && (
                      <div className="text-xs text-slate-400 mt-2">Coming Soon</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Connection Method */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Connection Method</h3>
              
              <div className="space-y-3">
                <label className={'flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ' + (
                  formData.connectionMethod === 'direct'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-700 bg-slate-700/30 hover:border-blue-500/50'
                )}>
                  <input
                    type="radio"
                    name="connectionMethod"
                    value="direct"
                    checked={formData.connectionMethod === 'direct'}
                    onChange={(e) => setFormData({ ...formData, connectionMethod: e.target.value })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-white">Direct Connection</div>
                    <div className="text-sm text-slate-400">Database has public IP or is in same network</div>
                  </div>
                </label>

                <label className={'flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ' + (
                  formData.connectionMethod === 'ssh_tunnel'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-700 bg-slate-700/30 hover:border-blue-500/50'
                )}>
                  <input
                    type="radio"
                    name="connectionMethod"
                    value="ssh_tunnel"
                    checked={formData.connectionMethod === 'ssh_tunnel'}
                    onChange={(e) => setFormData({ ...formData, connectionMethod: e.target.value })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-white">SSH Tunnel (Recommended for on-prem)</div>
                    <div className="text-sm text-slate-400">Connect through SSH server (most secure)</div>
                  </div>
                </label>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div className="text-sm text-blue-300">
                    SSH Tunnel allows secure connection to databases behind firewalls without exposing them to the internet.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Configuration */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Database Configuration</h3>
              
              {/* Connection Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Connection Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., School District DB"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this connection"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Database Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Host *</label>
                  <input
                    type="text"
                    value={formData.host}
                    onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                    placeholder="192.168.1.100 or db.example.com"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Port *</label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Database Name *</label>
                  <input
                    type="text"
                    value={formData.database}
                    onChange={(e) => setFormData({ ...formData, database: e.target.value })}
                    placeholder="school_db"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="readonly_user"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* SSH Tunnel Configuration */}
              {formData.connectionMethod === 'ssh_tunnel' && (
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="text-md font-semibold text-white mb-3">SSH Tunnel Configuration</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">SSH Host *</label>
                      <input
                        type="text"
                        value={formData.sshHost}
                        onChange={(e) => setFormData({ ...formData, sshHost: e.target.value })}
                        placeholder="ssh.example.com"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">SSH Port</label>
                      <input
                        type="number"
                        value={formData.sshPort}
                        onChange={(e) => setFormData({ ...formData, sshPort: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">SSH Username *</label>
                    <input
                      type="text"
                      value={formData.sshUsername}
                      onChange={(e) => setFormData({ ...formData, sshUsername: e.target.value })}
                      placeholder="admin"
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">SSH Private Key *</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        accept=".pem,.key"
                        className="hidden"
                        id="ssh-key-upload"
                      />
                      <label
                        htmlFor="ssh-key-upload"
                        className="flex items-center space-x-2 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white cursor-pointer hover:bg-slate-600 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload Key File</span>
                      </label>
                      {formData.sshPrivateKey && (
                        <span className="text-sm text-green-400 flex items-center space-x-1">
                          <CheckCircle className="w-4 h-4" />
                          <span>Key loaded</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Upload your private key file (.pem or .key)</p>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="flex items-center space-x-6 pt-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sslEnabled}
                    onChange={(e) => setFormData({ ...formData, sslEnabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-slate-300">Use SSL/TLS</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.readOnly}
                    onChange={(e) => setFormData({ ...formData, readOnly: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-slate-300">Read-only mode (recommended)</span>
                </label>
              </div>
            </div>
          )}

          {/* Step 4: Test & Save */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Test Connection</h3>
              
              {!testResult && (
                <div className="text-center py-8">
                  <p className="text-slate-400 mb-4">Click the button below to test your connection</p>
                  <button
                    onClick={handleTestConnection}
                    disabled={loading}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Testing Connection...</span>
                      </>
                    ) : (
                      <>
                        <Database className="w-5 h-5" />
                        <span>Test Connection</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {testResult && (
                <div className={'p-6 rounded-lg border-2 ' + (
                  testResult.success
                    ? 'bg-green-500/10 border-green-500'
                    : 'bg-red-500/10 border-red-500'
                )}>
                  <div className="flex items-start space-x-3">
                    {testResult.success ? (
                      <CheckCircle className="w-6 h-6 text-green-400 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-400 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={'font-semibold mb-2 ' + (
                        testResult.success ? 'text-green-400' : 'text-red-400'
                      )}>
                        {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
                      </h4>
                      {testResult.success ? (
                        <div className="space-y-2 text-sm text-slate-300">
                          <div className="flex justify-between">
                            <span>Latency:</span>
                            <span className="font-mono">{testResult.latency}ms</span>
                          </div>
                          {testResult.details && (
                            <>
                              <div className="flex justify-between">
                                <span>Database:</span>
                                <span className="font-mono">{testResult.details.database}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Version:</span>
                                <span className="font-mono text-xs">{testResult.details.version}</span>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-red-300">{testResult.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {testResult && !testResult.success && (
                <button
                  onClick={handleTestConnection}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 p-6 flex items-center justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{step === 1 ? 'Cancel' : 'Back'}</span>
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !formData.dbType) ||
                (step === 3 && (!formData.name || !formData.host || !formData.database || !formData.username || !formData.password))
              }
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={!testResult || !testResult.success || loading}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Save Connection</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
